import useResizeObserver from '@react-hook/resize-observer';
import debounce from 'lodash/debounce';
import { RefObject, useLayoutEffect, useMemo, useState } from 'react';

export default function useWidth(target: RefObject<HTMLElement>): number {
    const [width, setWidth] = useState<number>();

    const safelySetWidth = useMemo(
        () =>
            debounce((targetEntry: { target: Element | undefined }) => {
                const target = targetEntry.target;

                // use `htmlElement` defensively as it could be undefined during some initial rendering sequence

                if (!target) return;

                // ensure `getBoundingClientRect` exists on the element
                if (typeof target.getBoundingClientRect !== 'function') {
                    return;
                }

                const elementRect = target.getBoundingClientRect();

                // use `elementRect` defensively as it can be undefined in some edge cases
                const maybeNewWidth = Math.floor(elementRect?.width);

                // if `elementRect` was undefined, `maybeNewWidth` will be unusable
                if (!(Number.isInteger(maybeNewWidth) && maybeNewWidth > 0)) {
                    return;
                }

                setWidth((prevWidth) => {
                    if (maybeNewWidth !== prevWidth) {
                        return maybeNewWidth;
                    }

                    return prevWidth;
                });
            }),
        []
    );

    // Set the initial width
    useLayoutEffect(() => {
        safelySetWidth({ target: target?.current });
    }, [target, safelySetWidth]);

    // Where the magic happens
    useResizeObserver(target, safelySetWidth);

    return width;
}
